import { snakeCase } from 'lodash';
import React, { ReactNode, useCallback, useContext, useMemo } from 'react';
// eslint-disable-next-line import/no-internal-modules
import { FormattedMessage } from 'react-intl';
import {
  FinancialsTooltipKeys,
  ProjectFinancialsContractType,
} from 'api/project';
import { currencyRenderer } from 'components/atoms/currency-renderer';
import { formatterWithMinutes } from 'components/atoms/input-duration/formatter';
import { PLACEHOLDER } from 'constant';
import { ProjectFinancialsContext } from 'contexts/project-financials';
import { FinancialStatsGridProps } from '../components/stats-grid';
import {
  FinancialStat,
  ProjectFinancialsBudgetCard,
  ProjectFinancialsComponentGroup,
  ProjectFinancialsHoursCard,
} from '../types';
import { projectFinancialsGaugeChartOptions } from '../utils/charts/gauge';
import { hoursToMins, percentageRenderer } from '../utils/common';
import { useProjectFinancialsLabels } from './labels';
import { useFinancialsTooltips } from './tooltips';

interface ReturnProps {
  budgetWidgetData: FinancialStatsGridProps;
  budgetWidgetTooltipContent: ReactNode;
  hoursWidgetData: FinancialStatsGridProps;
  nonBillableStats: FinancialStat[];
}

export const useProjectFinancialsWidgets = (): ReturnProps => {
  const context = useContext(ProjectFinancialsContext);
  const {
    currencyCode,
    projectFinancials: {
      revenue,
      cost,
      profit,
      margin,
      timeTracked,
      timeAllocated,
      billableTimeTracked,
      nonBillableTimeTracked,
      projectFinancialDetails: {
        projectBudget,
        projectHours,
        financialContractType,
      },
    },
    projectFinancialsVisibilityConfig: { profitabilityData, allocationData },
    permissionsMap: {
      projectProfitability: { canRead: canViewProfitability },
    },
    revenueLabels,
    timeLabels,
  } = context;

  const {
    projectBudgetLabel,
    projectBudgetRemainingLabel,
    revenueLabel,
    projectHoursLabel,
    projectHoursRemainingLabel,
    timeTrackedLabel,
  } = useProjectFinancialsLabels({
    financialContractType,
  });

  const { getTooltipContent } = useFinancialsTooltips({
    financialContractType,
  });

  const getChartOptions = useMemo(
    () =>
      projectFinancialsGaugeChartOptions({
        financialContractType,
        currencyCode,
        revenueLabels,
        timeLabels,
        percentageLabels: [],
      }),
    [currencyCode, financialContractType, revenueLabels, timeLabels],
  );

  const asProjectFinancialStat = useCallback(
    ({
      type,
      ...data
    }:
      | ProjectFinancialsBudgetCard
      | ProjectFinancialsHoursCard): FinancialStat[] =>
      Object.keys(data).map((key) => ({
        label: (
          <FormattedMessage
            id={`project.financials.${financialContractType}.${snakeCase(key)}`}
          />
        ),
        value:
          data[key] == null
            ? PLACEHOLDER
            : type === ProjectFinancialsComponentGroup.Budget
            ? key === 'margin'
              ? percentageRenderer(data[key])
              : currencyRenderer(data[key], currencyCode)
            : formatterWithMinutes(data[key]),
      })),
    [currencyCode, financialContractType],
  );

  const budgetWidgetData = useMemo(
    (): FinancialStatsGridProps => ({
      stats: asProjectFinancialStat({
        type: ProjectFinancialsComponentGroup.Budget,
        ...(financialContractType === ProjectFinancialsContractType.TM
          ? { revenue }
          : financialContractType === ProjectFinancialsContractType.FIXED
          ? { projectBudget }
          : {}),
        ...(canViewProfitability && profitabilityData
          ? {
              cost,
              profit,
              margin,
            }
          : {}),
      }),
      chartOptions:
        revenue != null && projectBudget != null
          ? getChartOptions({
              type: ProjectFinancialsComponentGroup.Budget,
              value: revenue,
              max: projectBudget, // to check with pm for cases when budgetedHours is not set,
              remainingValue: projectBudget - revenue,
              valueLabel: revenueLabel,
              maxLabel: projectBudgetLabel,
              remainingLabel: projectBudgetRemainingLabel,
            })
          : undefined,
    }),
    [
      asProjectFinancialStat,
      canViewProfitability,
      cost,
      financialContractType,
      getChartOptions,
      margin,
      profit,
      profitabilityData,
      projectBudget,
      projectBudgetLabel,
      projectBudgetRemainingLabel,
      revenue,
      revenueLabel,
    ],
  );

  const budgetWidgetTooltipContent = useMemo(
    () =>
      getTooltipContent([
        'revenue',
        ...(canViewProfitability && profitabilityData
          ? ['cost', 'profit', 'margin']
          : []),
      ] as FinancialsTooltipKeys[]),
    [canViewProfitability, getTooltipContent, profitabilityData],
  );

  const hoursWidgetData = useMemo(
    (): FinancialStatsGridProps => ({
      stats: asProjectFinancialStat({
        type: ProjectFinancialsComponentGroup.Hours,
        ...(allocationData ? { timeAllocated } : {}),
        billableTimeTracked,
        nonBillableTimeTracked,
      }),
      ...(timeTracked != null && projectHours != null
        ? {
            chartOptions: getChartOptions({
              type: ProjectFinancialsComponentGroup.Hours,
              value: timeTracked,
              max: hoursToMins(projectHours),
              remainingValue: hoursToMins(projectHours) - timeTracked,
              valueLabel: timeTrackedLabel,
              maxLabel: projectHoursLabel,
              remainingLabel: projectHoursRemainingLabel,
            }),
          }
        : {}),
    }),
    [
      allocationData,
      asProjectFinancialStat,
      billableTimeTracked,
      getChartOptions,
      nonBillableTimeTracked,
      projectHours,
      projectHoursLabel,
      projectHoursRemainingLabel,
      timeAllocated,
      timeTracked,
      timeTrackedLabel,
    ],
  );

  const nonBillableStats: FinancialStat[] = useMemo(() => {
    if (financialContractType === ProjectFinancialsContractType.NONBILLABLE) {
      return [
        ...(canViewProfitability && profitabilityData && cost != null
          ? [
              {
                label: (
                  <FormattedMessage
                    id={`project.financials.${financialContractType}.${snakeCase(
                      'cost',
                    )}`}
                  />
                ),
                value: currencyRenderer(cost, currencyCode),
              },
            ]
          : []),

        {
          label: (
            <FormattedMessage
              id={`project.financials.${financialContractType}.${snakeCase(
                'projectHours',
              )}`}
            />
          ),
          value: formatterWithMinutes(hoursToMins(projectHours ?? 0)),
        },
        ...(allocationData
          ? [
              {
                label: (
                  <FormattedMessage
                    id={`project.financials.${financialContractType}.${snakeCase(
                      'timeAllocated',
                    )}`}
                  />
                ),
                value: formatterWithMinutes(timeAllocated ?? 0),
              },
            ]
          : []),
        {
          label: (
            <FormattedMessage
              id={`project.financials.${financialContractType}.${snakeCase(
                'nonBillableTimeTracked',
              )}`}
            />
          ),
          value: formatterWithMinutes(nonBillableTimeTracked ?? 0),
        },
      ];
    }

    return [];
  }, [
    allocationData,
    canViewProfitability,
    cost,
    currencyCode,
    financialContractType,
    nonBillableTimeTracked,
    profitabilityData,
    projectHours,
    timeAllocated,
  ]);

  return {
    budgetWidgetData,
    budgetWidgetTooltipContent,
    hoursWidgetData,
    nonBillableStats,
  };
};
